import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InstallationChain } from '@/features/installations/InstallationChain';
import { inventoryErrorKey } from './inventoryErrors';
import {
  Caution,
  Dialog,
  ErrorNote,
  Field,
  INPUT,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  WhenAndNote,
  formatInstant,
  toIsoInstant,
} from './registerUi';
import {
  isReadyToSwapIn,
  isSimAvailable,
  useCreateDevice,
  useDecommissionDevice,
  useDeviceAssignments,
  useInstallSim,
  useRemoveSim,
  useSwapVehicleDevice,
  useUpdateDevice,
  type AdminDevice,
  type AdminSim,
  type DeviceStatus,
} from './useInventory';

const DEVICE_STATUSES: readonly DeviceStatus[] = ['in_service', 'retired', 'faulty'];

/**
 * Register a tracker — the warehouse act, before any customer is involved.
 *
 * 🔴 NO TRACCAR-ID FIELD, AND NO PROTOCOL FIELD. The Traccar id is minted at
 * provisioning and stamped by the server; a human typing one could only be
 * copying it out of Traccar's UI, and a wrong copy points our row at somebody
 * else's device. The protocol belongs to the INSTALL, not to the box, and the
 * wizard collects it there.
 *
 * `onCreated` exists for the wizard's escape hatch: an installer standing at a
 * truck with an unregistered box registers it inline and carries straight on.
 */
export function NewDeviceDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated?: (id: string) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateDevice();
  const [imei, setImei] = useState('');
  const [model, setModel] = useState('');
  const [firmware, setFirmware] = useState('');
  const [notes, setNotes] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);

  async function submit() {
    setErrorKey(null);
    try {
      const res = await create.mutateAsync({
        imei: imei.trim(),
        model: model.trim() || null,
        firmware: firmware.trim() || null,
        notes: notes.trim() || null,
      });
      onCreated?.(res.id);
      onClose();
    } catch (e) {
      setErrorKey(inventoryErrorKey(e));
    }
  }

  return (
    <Dialog
      title={t('inventory.devices.new')}
      onClose={onClose}
      footer={
        <button
          type="button"
          className={PRIMARY_BUTTON}
          disabled={imei.trim().length < 6 || create.isPending}
          onClick={() => void submit()}
        >
          {create.isPending ? t('inventory.saving') : t('inventory.devices.register')}
        </button>
      }
    >
      <div className="grid gap-4">
        <Field label={t('inventory.devices.imei')} hint={t('inventory.devices.imeiHint')}>
          <input
            className={INPUT}
            value={imei}
            onChange={(e) => setImei(e.target.value)}
            inputMode="numeric"
            autoFocus
          />
        </Field>
        <Field label={t('inventory.devices.model')}>
          <input
            className={INPUT}
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="FMC920"
          />
        </Field>
        <Field label={t('inventory.devices.firmware')}>
          <input className={INPUT} value={firmware} onChange={(e) => setFirmware(e.target.value)} />
        </Field>
        <Field label={t('inventory.notes')}>
          <input className={INPUT} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
      <ErrorNote errorKey={errorKey} />
    </Dialog>
  );
}

/**
 * Edit what can change about a box: model, firmware, condition, notes.
 *
 * 🔴 RETIRE IS REFUSED WHILE THE TRACKER IS FITTED (18g), AND THIS SCREEN SAYS
 * SO BEFORE THE SERVER DOES.
 *
 * Nothing used to refuse it, so a super-admin could mark the tracker in a paying
 * customer's truck `retired` while it kept reporting, kept being routed and kept
 * writing that customer's trips — the register saying the unit was out of
 * service while the fleet said otherwise. `updateDevice` now throws
 * `device_still_installed`.
 *
 * 🔴 `retired` IS STILL OFFERED IN THE SELECT, DELIBERATELY, and that is NOT an
 * "absence over disabled" violation. The rule governs controls for features that
 * do not exist; retiring exists and has ONE UNMET PRECONDITION, whose reason is
 * rendered right beside it the instant the value is chosen, and which the
 * operator can resolve today by taking the tracker out. Removing the option
 * would teach nothing except that the list is short.
 *
 * 🔴 ONLY `retired`, NOT `faulty`. They are different statements: `faulty` is a
 * fact about a unit that is usually DISCOVERED while it is in a customer's
 * vehicle, and refusing to record it would push the operator into leaving the
 * register wrong. The server draws the line in exactly the same place, and the
 * caution says both halves so the two cannot be confused.
 */
export function EditDeviceDialog({
  device,
  onClose,
}: {
  device: AdminDevice;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateDevice();
  const [model, setModel] = useState(device.model ?? '');
  const [firmware, setFirmware] = useState(device.firmware ?? '');
  const [status, setStatus] = useState<DeviceStatus>(device.status);
  const [notes, setNotes] = useState(device.notes ?? '');
  const [errorKey, setErrorKey] = useState<string | null>(null);

  async function submit() {
    setErrorKey(null);
    try {
      await update.mutateAsync({
        deviceId: device.id,
        body: {
          model: model.trim() || null,
          firmware: firmware.trim() || null,
          status,
          notes: notes.trim() || null,
        },
      });
      onClose();
    } catch (e) {
      setErrorKey(inventoryErrorKey(e));
    }
  }

  return (
    <Dialog
      title={t('inventory.devices.edit', { imei: device.imei })}
      onClose={onClose}
      footer={
        <button
          type="button"
          className={PRIMARY_BUTTON}
          disabled={update.isPending}
          onClick={() => void submit()}
        >
          {update.isPending ? t('inventory.saving') : t('inventory.save')}
        </button>
      }
    >
      <div className="grid gap-4">
        {/* The IMEI is shown and not editable — it is the device's identity, and
            every historical assignment resolves through it. There is no `imei`
            field on the PATCH schema at all, so this is not a UI choice. */}
        <p className="text-[12.5px] text-raad-muted">
          {t('inventory.devices.imei')}: <span className="num text-raad-text">{device.imei}</span>
        </p>
        <Field label={t('inventory.devices.model')}>
          <input className={INPUT} value={model} onChange={(e) => setModel(e.target.value)} />
        </Field>
        <Field label={t('inventory.devices.firmware')}>
          <input className={INPUT} value={firmware} onChange={(e) => setFirmware(e.target.value)} />
        </Field>
        <Field label={t('inventory.status')}>
          <select
            className={INPUT}
            value={status}
            onChange={(e) => setStatus(e.target.value as DeviceStatus)}
          >
            {DEVICE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`inventory.deviceStatus.${s}`)}
              </option>
            ))}
          </select>
        </Field>
        {/* The precondition, beside the control, the moment it becomes relevant —
            see the header. Not shown for `faulty`, which IS allowed while
            fitted. */}
        {status === 'retired' && device.vehicleId !== null ? (
          <Caution>
            {t('inventory.devices.retireWhileFitted', {
              plate: device.vehiclePlate ?? t('inventory.unknown'),
            })}
          </Caution>
        ) : null}
        <Field label={t('inventory.notes')}>
          <input className={INPUT} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
      <ErrorNote errorKey={errorKey} />
    </Dialog>
  );
}

/**
 * Fit a SIM into a device, or take the fitted one back to stock.
 *
 * One dialog for both directions because they are one question — "what is in
 * this box?" — and a device can only be in one of the two states. Removing does
 * NOT delete the assignment row: it closes it, and the closed row is the record
 * that the SIM was in this device between two instants.
 */
export function DeviceSimDialog({
  device,
  sims,
  onClose,
}: {
  device: AdminDevice;
  sims: AdminSim[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const install = useInstallSim();
  const remove = useRemoveSim();
  const [simId, setSimId] = useState('');
  const [at, setAt] = useState('');
  const [note, setNote] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const fitted = device.simId !== null;
  const available = sims.filter(isSimAvailable);
  const chosen = available.find((s) => s.id === simId) ?? null;

  async function submit() {
    setErrorKey(null);
    const when = toIsoInstant(at);
    try {
      if (fitted) {
        await remove.mutateAsync({
          deviceId: device.id,
          body: { ...(when ? { at: when } : {}), note: note.trim() || null },
        });
      } else {
        await install.mutateAsync({
          deviceId: device.id,
          body: { simId, ...(when ? { at: when } : {}), note: note.trim() || null },
        });
      }
      onClose();
    } catch (e) {
      setErrorKey(inventoryErrorKey(e));
    }
  }

  const pending = install.isPending || remove.isPending;

  return (
    <Dialog
      title={
        fitted
          ? t('inventory.devices.removeSimTitle', { imei: device.imei })
          : t('inventory.devices.installSimTitle', { imei: device.imei })
      }
      onClose={onClose}
      footer={
        <button
          type="button"
          className={PRIMARY_BUTTON}
          disabled={pending || (!fitted && simId === '')}
          onClick={() => void submit()}
        >
          {pending
            ? t('inventory.saving')
            : fitted
              ? t('inventory.devices.removeSim')
              : t('inventory.devices.installSim')}
        </button>
      }
    >
      <div className="grid gap-4">
        {fitted ? (
          <p className="text-[13px]">
            {t('inventory.devices.currentSim')}:{' '}
            <span className="num">{device.simIccid}</span>
            {device.simMsisdn ? <span className="num"> · {device.simMsisdn}</span> : null}
            {device.simCarrier ? <span className="text-raad-muted"> · {device.simCarrier}</span> : null}
          </p>
        ) : available.length === 0 ? (
          <p className="text-[13px] text-raad-muted">{t('inventory.devices.noSimsInStock')}</p>
        ) : (
          <>
            <Field label={t('inventory.devices.chooseSim')}>
              <select className={INPUT} value={simId} onChange={(e) => setSimId(e.target.value)}>
                <option value="">{t('inventory.devices.chooseSimPlaceholder')}</option>
                {available.map((s) => (
                  <option key={s.id} value={s.id}>
                    {`${s.iccid} · ${s.carrier}${s.msisdn ? ` · ${s.msisdn}` : ''}`}
                  </option>
                ))}
              </select>
            </Field>
            {/* A SIM with no number can be fitted — it is a real physical act and
                refusing it would push the operator to record it nowhere. It is
                said out loud instead, because a device on an unactivated SIM
                will never report. */}
            {chosen !== null && chosen.msisdn === null ? (
              <Caution>{t('inventory.sims.notActivatedWarning')}</Caution>
            ) : null}
          </>
        )}
        <WhenAndNote at={at} note={note} onAt={setAt} onNote={setNote} />
      </div>
      <ErrorNote errorKey={errorKey} />
    </Dialog>
  );
}

/**
 * Replace the tracker fitted to a vehicle.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 THE CAUTION IS THE POINT OF THIS DIALOG, NOT DECORATION.
 * ─────────────────────────────────────────────────────────────────────────────
 * The trip detector resolves device → vehicle through the CURRENT mapping. Move
 * a tracker to another vehicle and any re-detection over the old window will
 * attribute the first vehicle's journeys to the second. Nothing in the schema
 * prevents it and nothing in the API refuses it, so the control is a sentence on
 * this screen — and if it is not in the UI it does not exist.
 *
 * 🔴 THERE IS NO "REMOVE TRACKER" ACTION, ANYWHERE, NOT EVEN DISABLED.
 * `vehicles.traccar_device_id` is NOT NULL and projects the open assignment: a
 * vehicle without a tracker has no honest representation, so there is no
 * endpoint and there is no button.
 */
export function SwapDeviceDialog({
  device,
  candidates,
  onClose,
}: {
  /** The device currently installed in the vehicle being re-fitted. */
  device: AdminDevice;
  candidates: AdminDevice[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const swap = useSwapVehicleDevice();
  const [deviceId, setDeviceId] = useState('');
  const [at, setAt] = useState('');
  const [note, setNote] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const eligible = candidates.filter((d) => isReadyToSwapIn(d) && d.id !== device.id);

  async function submit() {
    if (device.vehicleId === null) return;
    setErrorKey(null);
    const when = toIsoInstant(at);
    try {
      await swap.mutateAsync({
        vehicleId: device.vehicleId,
        deviceId,
        ...(when ? { at: when } : {}),
        note: note.trim() || null,
      });
      onClose();
    } catch (e) {
      setErrorKey(inventoryErrorKey(e));
    }
  }

  return (
    <Dialog
      title={t('inventory.devices.swapTitle', { plate: device.vehiclePlate ?? '' })}
      onClose={onClose}
      footer={
        <button
          type="button"
          className={PRIMARY_BUTTON}
          disabled={swap.isPending || deviceId === ''}
          onClick={() => void submit()}
        >
          {swap.isPending ? t('inventory.saving') : t('inventory.devices.swap')}
        </button>
      }
    >
      <div className="grid gap-4">
        <Caution>{t('inventory.devices.swapCaution')}</Caution>
        <p className="text-[13px]">
          {t('inventory.devices.currentTracker')}: <span className="num">{device.imei}</span>
        </p>
        {eligible.length === 0 ? (
          // Said, rather than shown as an empty dropdown. A control that offers
          // nothing with no explanation is the failure this codebase keeps
          // paying for.
          <p className="text-[13px] text-raad-muted">{t('inventory.devices.noSwapCandidates')}</p>
        ) : (
          <Field label={t('inventory.devices.newTracker')}>
            <select
              className={INPUT}
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
            >
              <option value="">{t('inventory.devices.chooseDevicePlaceholder')}</option>
              {eligible.map((d) => (
                <option key={d.id} value={d.id}>
                  {`${d.imei}${d.model ? ` · ${d.model}` : ''}`}
                </option>
              ))}
            </select>
          </Field>
        )}
        <WhenAndNote at={at} note={note} onAt={setAt} onNote={setNote} />
      </div>
      <ErrorNote errorKey={errorKey} />
    </Dialog>
  );
}

/** One row of history: an interval, and what it was attached to. */
function HistoryRow({
  primary,
  secondary,
  fromAt,
  toAt,
  note,
}: {
  primary: string;
  secondary: string | null;
  fromAt: string;
  toAt: string | null;
  note: string | null;
}) {
  const { t, i18n } = useTranslation();
  return (
    <li className="border-b border-raad-border py-3 last:border-b-0">
      <p className="text-[13px] font-medium">
        <span className="num">{primary}</span>
        {secondary ? <span className="text-raad-muted"> · {secondary}</span> : null}
      </p>
      <p className="mt-0.5 text-[12px] text-raad-muted">
        {formatInstant(fromAt, i18n.language)}
        {' → '}
        {/* An open interval is "still fitted", never a blank or a dash — the
            absence of an end date is the most load-bearing fact in the row. */}
        {toAt === null ? (
          <span className="text-raad-accent">{t('inventory.stillFitted')}</span>
        ) : (
          formatInstant(toAt, i18n.language)
        )}
      </p>
      {note ? <p className="mt-0.5 text-[12px] text-raad-muted">{note}</p> : null}
    </li>
  );
}

/**
 * A device's two histories: the vehicles it has been in, and the SIMs that have
 * been in it. Newest first, straight off `GET /admin/devices/:id/assignments`.
 */
/**
 * WHAT THIS TRACKER HAS BEEN FITTED TO, AND WHAT SIM IT CARRIED — the BODY,
 * without a dialog around it.
 *
 * 🔴 SPLIT OUT IN SLICE 18f so the `/vehicles` row detail can show the same
 * history inline. Copied instead, one of the two would keep the "still fitted"
 * treatment of an open interval and the other would render a blank end date —
 * and the absence of an end date is the most load-bearing fact in the row.
 *
 * 🔴 SUPER-ADMIN ONLY BY THE ROUTE IT READS. `GET /admin/devices/:id/assignments`
 * is `requireSuperAdmin`, so a caller must not render this for an `admin_staff`:
 * it would fetch a 403 and show an error where there is no fault.
 */
export function DeviceHistory({ deviceId }: { deviceId: string }) {
  const { t } = useTranslation();
  const q = useDeviceAssignments(deviceId);

  return (
    <>
      {q.isPending ? (
        <p className="text-sm text-raad-muted">{t('inventory.loading')}</p>
      ) : q.isError || q.data === undefined ? (
        <p role="alert" className="text-sm text-raad-error">
          {t('inventory.historyError')}
        </p>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <h3 className="text-[13px] font-semibold">{t('inventory.devices.vehicleHistory')}</h3>
            {q.data.vehicles.length === 0 ? (
              <p className="mt-2 text-[12.5px] text-raad-muted">{t('inventory.devices.neverFitted')}</p>
            ) : (
              <ul className="mt-1">
                {q.data.vehicles.map((a) => (
                  <HistoryRow
                    key={a.id}
                    primary={a.vehiclePlate ?? t('inventory.unknown')}
                    secondary={null}
                    fromAt={a.fromAt}
                    toAt={a.toAt}
                    note={a.note}
                  />
                ))}
              </ul>
            )}
          </div>
          <div>
            <h3 className="text-[13px] font-semibold">{t('inventory.devices.simHistory')}</h3>
            {q.data.sims.length === 0 ? (
              <p className="mt-2 text-[12.5px] text-raad-muted">{t('inventory.devices.neverHadSim')}</p>
            ) : (
              <ul className="mt-1">
                {q.data.sims.map((a) => (
                  <HistoryRow
                    key={a.id}
                    primary={a.simIccid ?? t('inventory.unknown')}
                    secondary={a.simMsisdn}
                    fromAt={a.fromAt}
                    toAt={a.toAt}
                    note={a.note}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * ─────────────────────────────────────────────────────────────────────────────
 * DECOMMISSION — SLICE 18g. Take the tracker OUT of the tracking engine and keep
 * every position it ever sent.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * 🔴 IT IS NOT A DELETE, AND THE DIALOG SAYS WHAT IT ACTUALLY DOES. Mahad,
 * 1 Sep: *"NEVER HARD DELETE. Always retire."* This removes the Traccar object so
 * the same box can be commissioned again later; the register row stays, the
 * `device_traccar_links` row is CLOSED rather than cleared, and every position
 * that tracker sent stays resolvable to its owner through that closed interval.
 * An operator who reads "decommission" and fears they are erasing a customer's
 * history is reading the button, so the sentence is on the screen.
 *
 * 🔴 A FITTED TRACKER NEVER REACHES THIS DIALOG. The button is absent unless the
 * device is commissioned AND in stock (`canDecommission`), which is exactly the
 * pair of preconditions the server checks before it touches Traccar. That is the
 * line protecting the pilot's live vehicle, and it is enforced server-side too —
 * this is the explanation, not the guard.
 *
 * 🔴 `alreadyGone` IS REPORTED, NOT SWALLOWED. Traccar answering 404 means the
 * device had already been removed — in Traccar's own UI, or by an earlier attempt
 * of this same call that died before its transaction committed. The decommission
 * still completes and the register is now correct, but a retry must not look
 * identical to a first success, so the dialog stays open and says which happened.
 */
export function DecommissionDeviceDialog({
  device,
  onClose,
}: {
  device: AdminDevice;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const decommission = useDecommissionDevice();
  const [at, setAt] = useState('');
  const [note, setNote] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [alreadyGone, setAlreadyGone] = useState(false);

  async function submit() {
    setErrorKey(null);
    const when = toIsoInstant(at);
    try {
      const res = await decommission.mutateAsync({
        deviceId: device.id,
        body: { ...(when ? { at: when } : {}), note: note.trim() || null },
      });
      // Held open ONLY for the surprising outcome. An ordinary success closes,
      // like every other write on this screen.
      if (res.alreadyGone) setAlreadyGone(true);
      else onClose();
    } catch (e) {
      setErrorKey(inventoryErrorKey(e));
    }
  }

  return (
    <Dialog
      title={t('inventory.devices.decommissionTitle', { imei: device.imei })}
      onClose={onClose}
      footer={
        alreadyGone ? (
          <button type="button" className={PRIMARY_BUTTON} onClick={onClose}>
            {t('inventory.close')}
          </button>
        ) : (
          <button
            type="button"
            className={PRIMARY_BUTTON}
            disabled={decommission.isPending}
            onClick={() => void submit()}
          >
            {decommission.isPending
              ? t('inventory.saving')
              : t('inventory.devices.decommissionConfirm')}
          </button>
        )
      }
    >
      <div className="grid gap-4">
        {alreadyGone ? (
          <Caution>{t('inventory.devices.decommissionAlreadyGone')}</Caution>
        ) : (
          <>
            {/* What it does, in words, before the press. */}
            <p className="text-[13px]">{t('inventory.devices.decommissionExplain')}</p>
            <WhenAndNote at={at} note={note} onAt={setAt} onNote={setNote} />
          </>
        )}

        {/* ──────────────────────────────────────────────────────────────────
            🔴 THE TRAP NOBODY WOULD GUESS, MEASURED ON PRODUCTION 1 SEP.
            ──────────────────────────────────────────────────────────────────
            Mahad decommissioned a LIVE, reporting tracker (Traccar id 6, 49
            positions, last fix 07:16:27), then re-commissioned the same box as
            id 8. id 8 received ZERO positions and nothing was stored anywhere
            after 07:16 — the hardware was fine and the register was correct.

            Traccar resolves a device ONCE, when the TCP session opens, and holds
            that resolution for the life of the session. The tracker's existing
            connection still pointed at the device object that had just been
            deleted, so every report it sent went nowhere. It cannot be recovered
            from this end: only the tracker can open a new session, and only a
            power-cycle (or `cpureset` by SMS) makes it do so.

            It is on THIS screen, unconditional and in both branches, because
            this is the moment the operator is holding the hardware. A release
            note nobody reads while standing at a vehicle is not a fix. It is
            phrased as an instruction rather than a caveat for the same reason.
            ────────────────────────────────────────────────────────────────── */}
        <Caution>
          <span className="block font-semibold">
            {t('inventory.devices.decommissionRestartTitle')}
          </span>
          <span className="mt-1 block">{t('inventory.devices.decommissionRestartBody')}</span>
        </Caution>
      </div>
      <ErrorNote errorKey={errorKey} />
    </Dialog>
  );
}

export function DeviceHistoryDialog({
  device,
  onClose,
}: {
  device: AdminDevice;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog title={t('inventory.devices.historyTitle', { imei: device.imei })} onClose={onClose} wide>
      <DeviceHistory deviceId={device.id} />
      {/* 🔴 THE INSTALL CHAIN (slice 30) — every install record this tracker has
          had across its relocations, newest first. NO IMEI on that wire. */}
      <div className="mt-5 border-t border-raad-border pt-4">
        <h3 className="mb-2 text-[13px] font-semibold">{t('installations.chain.heading')}</h3>
        <InstallationChain deviceId={device.id} />
      </div>
      <div className="mt-5 flex justify-end">
        <button type="button" className={SECONDARY_BUTTON} onClick={onClose}>
          {t('inventory.close')}
        </button>
      </div>
    </Dialog>
  );
}
